import axios from 'axios';
import { getHeaders } from './header';
import { getUrl } from './url';
import {
  parseResponse,
  parseError,
  parseFetchResponse,
  parseFetchError,
} from './response';
import { urlQuery, normalizeParams } from './params';
import { SnakeCasesOptions } from '@/utils/case_style';
import { useFormErrors } from '@/models/global/formErrors';

let localSetFormErrors: (errors: { id: string; message: string }[]) => void;

export const request = {
  async get<T>(path: string, params?: T, options?: { signal?: AbortSignal }) {
    localSetFormErrors([]);
    if (params) {
      path = `${path}?${urlQuery(params)}`;
    }
    const headers = await getHeaders();
    const url = getUrl(path);
    try {
      const res = await axios.get(url, {
        headers: headers,
        signal: options?.signal,
      });
      return parseResponse(res);
    } catch (error) {
      return parseError(error, localSetFormErrors);
    }
  },
  async put<T>(
    path: string,
    params?: T,
    options?: { snakeCasesOptions?: SnakeCasesOptions }
  ) {
    localSetFormErrors([]);
    const headers = await getHeaders();
    const url = getUrl(path);
    try {
      const res = await axios.put(
        url,
        normalizeParams(params, options?.snakeCasesOptions),
        { headers: headers }
      );
      return parseResponse(res);
    } catch (error) {
      return parseError(error, localSetFormErrors);
    }
  },
  async post<T>(
    path: string,
    params?: T,
    options?: {
      signal?: AbortSignal;
      snakeCasesOptions?: SnakeCasesOptions;
    }
  ) {
    localSetFormErrors([]);
    const headers = await getHeaders();
    const url = getUrl(path);
    try {
      const res = await axios.post(
        url,
        normalizeParams(params, options?.snakeCasesOptions),
        {
          headers: headers,
          signal: options?.signal,
        }
      );
      return parseResponse(res);
    } catch (error) {
      return parseError(error, localSetFormErrors);
    }
  },
  async delete<T>(path: string, params?: T) {
    localSetFormErrors([]);
    const headers = await getHeaders();
    if (params) {
      path = `${path}?${urlQuery(params)}`;
    }
    const url = getUrl(path);
    try {
      const res = await axios.delete(url, { headers: headers });
      return parseResponse(res);
    } catch (error) {
      return parseError(error, localSetFormErrors);
    }
  },
  // fetchストリーミング対応（非Axios）
  async streamPost(
    path: string,
    params?: any,
    options?: { signal?: AbortSignal }
  ) {
    localSetFormErrors([]);
    const headers = await getHeaders();
    const url = getUrl(path);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/x-ndjson, application/json',
          ...headers,
        },
        body: JSON.stringify(params),
        signal: options?.signal,
      });
      return parseFetchResponse(res, localSetFormErrors);
    } catch (error) {
      // fetch のエラーはリクエスト情報を持たないため、失敗したリクエストの情報を渡す
      return parseFetchError(error, localSetFormErrors, {
        url,
        method: 'POST',
        organization: params?.organization_label,
      });
    }
  },
  inject() {
    const { setFormErrors } = useFormErrors();
    localSetFormErrors = setFormErrors;
  },
};
